import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/_services/user.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  apiBaseUrl = environment.apiBaseUrl;
  private accessTokenSubject: BehaviorSubject<string | null>;
  public accessTokenObservable: Observable<string | null>;

  public fcmTokenSubject: BehaviorSubject<string | null>;
  public fcmTokenObservable: Observable<string | null>;

  public deviceIdSubject: BehaviorSubject<string | null>;
  public deviceIdObservable: Observable<string | null>;

  constructor(
    private _router: Router,
    private _httpClient: HttpClient,
    private _userService: UserService,
  ) {
    this.accessTokenSubject = new BehaviorSubject<string | null>(localStorage.getItem('accessToken'));
    this.accessTokenObservable = this.accessTokenSubject.asObservable();

    this.fcmTokenSubject = new BehaviorSubject<string | null>(null);
    this.fcmTokenObservable = this.fcmTokenSubject.asObservable();

    this.deviceIdSubject = new BehaviorSubject<string | null>(null);
    this.deviceIdObservable = this.deviceIdSubject.asObservable();
  }

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);

    this.accessTokenSubject.next(token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  set deviceId(deviceId: string) {
    localStorage.setItem('deviceId', deviceId);
  }

  get deviceId(): string {
    return localStorage.getItem('deviceId') ?? 'deviceId';
  }

  set fcmToken(fcmToken: string) {
    localStorage.setItem('fcmToken', fcmToken);
  }

  get fcmToken(): string {
    return localStorage.getItem('fcmToken') ?? 'fcmToken';
  }

  signIn(credentials: { email: string, password: string }) {
    return this._httpClient.post(`${this.apiBaseUrl}/auth/signIn`, credentials).pipe(
      switchMap((response: any) => {
        const { isSuccess, data } = response;
        if(isSuccess) {
          const { user, token } = data;

          // Store the access token in the local storage
          this.accessToken = token.accessToken;

          // Store the user on the user service
          this._userService.user = user;

          // Return a new observable with the response
          return of(response);
        } else {
          return of(response);
        }
      })
    );
  }

  forgotPassword(params: { email: string }) {
    return this._httpClient.post(`${this.apiBaseUrl}/auth/forgotPassword`, params);
  }

  resetPassword(params: { token: string, newPassword: string }) {
    return this._httpClient.put(`${this.apiBaseUrl}/auth/resetPassword`, params);
  }

  changepassword(params: { currentPassword: string, newPassword: string }) {
    return this._httpClient.put(`${this.apiBaseUrl}/auth/changePassword`, params );
  }

  validatePasswordResetToken(token: string) {
    return this._httpClient.get(`${this.apiBaseUrl}/auth/validate/passwordResetToken/${token}`);
  }

  delete(id: string) {
    return this._httpClient.delete(`${this.apiBaseUrl}/auth/delete/${id}`);
  }

  fcm(params: { deviceId: string, fcm: string }) {
    return this._httpClient.post(`${this.apiBaseUrl}/auth/fcm`, params );
  }

  logout(params: { deviceId: string }): void {
    this._httpClient.put(`${this.apiBaseUrl}/auth/logout`, params).subscribe({
      next: (response: any) => {
        this.logoutCallback();
      },
      error: (errorResponse: any) => {
        this.logoutCallback();
      },
      complete : () => {
        this.logoutCallback();
      }
    });
  }

  logoutCallback() {
    localStorage.removeItem('accessToken');

    localStorage.removeItem('deviceId');

    localStorage.removeItem('fcmToken');

    localStorage.removeItem('config');

    this.accessTokenSubject.next(null);

    this._userService.clearUser();

    this._router.navigate(['/login']);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if ( this.accessTokenSubject.value )
    {
      return of(true);
    }

    return of(false);
  }
  
}
